// Shared rules for every photo a person picks in Haastia. The server enforces
// the same limit (server/utils/imagePayloadValidation.js), so a photo that
// passes here is not refused there.

export const MAX_IMAGE_UPLOAD_BYTES = 10 * 1024 * 1024;
export const MAX_IMAGE_UPLOAD_LABEL = "10 MB";
export const IMAGE_TOO_LARGE_MESSAGE =
  "This image is larger than 10 MB. Please choose a smaller image.";
export const IMAGE_UNSUPPORTED_MESSAGE =
  "Unsupported image type. Use a PNG, JPG, WEBP, or GIF file.";
export const IMAGE_HEIC_MESSAGE =
  "HEIC photos aren't supported here. Choose the photo again from your phone's photo picker, or save it as a JPG first.";

// Photos are shrunk in the browser before they are sent. Nothing on Haastia
// shows a photo wider than this, and a 10 MB phone photo becomes a 1-2 MB
// request instead of a 13 MB one. The server resizes again as a safety net.
export const IMAGE_UPLOAD_MAX_DIMENSION = 2400;
const IMAGE_UPLOAD_QUALITY = 0.9;
const IMAGE_DECODE_TIMEOUT_MS = 10000;

const HEIC_TYPES = new Set(["image/heic", "image/heif", "image/heic-sequence", "image/heif-sequence"]);

export const isHeicFile = (file) => {
  const type = String(file?.type || "").toLowerCase();
  return HEIC_TYPES.has(type) || /\.(heic|heif)$/i.test(String(file?.name || ""));
};

// Returns a user-facing message, or "" when the file is fine to send.
export const validateImageFile = (file, { acceptedTypes } = {}) => {
  if (!file) return "Choose an image to upload.";
  if (isHeicFile(file)) return IMAGE_HEIC_MESSAGE;

  const type = String(file.type || "").toLowerCase();
  const allowed = acceptedTypes
    ? [...acceptedTypes].map((value) => String(value).trim().toLowerCase())
    : null;
  const typeOk = allowed ? allowed.includes(type) : type.startsWith("image/");
  if (!typeOk) return IMAGE_UNSUPPORTED_MESSAGE;

  if (Number(file.size || 0) > MAX_IMAGE_UPLOAD_BYTES) return IMAGE_TOO_LARGE_MESSAGE;
  return "";
};

export const readFileAsDataUri = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== "string" || !reader.result.trim()) {
        reject(new Error("We couldn't read that image. Try a different file."));
        return;
      }
      resolve(reader.result);
    };
    reader.onerror = () => reject(new Error("We couldn't read that image. Try a different file."));
    reader.readAsDataURL(file);
  });

const loadImageElement = (dataUri) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const timeoutId = setTimeout(() => reject(new Error("Timed out reading image.")), IMAGE_DECODE_TIMEOUT_MS);
    image.onload = () => {
      clearTimeout(timeoutId);
      resolve(image);
    };
    image.onerror = () => {
      clearTimeout(timeoutId);
      reject(new Error("We couldn't read that image. Try a different file."));
    };
    image.src = dataUri;
  });

/* Reads a picked photo as a data URI, shrinking it first when its longest side
   is over the limit. Smaller photos are sent untouched (never enlarged or
   re-encoded), GIFs are left alone so animation survives, and any failure to
   resize falls back to the original, which the server still accepts. */
export const prepareImageDataUri = async (file, { maxDimension = IMAGE_UPLOAD_MAX_DIMENSION } = {}) => {
  const originalDataUri = await readFileAsDataUri(file);
  const sourceType = String(file?.type || "").toLowerCase();
  if (sourceType === "image/gif") return originalDataUri;

  try {
    // Checked before decoding: without a usable canvas the resize is pointless
    // and there is no reason to wait on the decode.
    const canvas = document.createElement("canvas");
    const context = canvas.getContext?.("2d");
    if (!context) return originalDataUri;

    const image = await loadImageElement(originalDataUri);
    const width = image.naturalWidth || 0;
    const height = image.naturalHeight || 0;
    const longest = Math.max(width, height);
    if (!longest || longest <= maxDimension) return originalDataUri;

    const scale = maxDimension / longest;
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    // The browser applies the photo's EXIF rotation when it decodes the image,
    // so the resized copy is upright without carrying the metadata.
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    // A canvas exported as JPEG turns transparency black, so PNG and WEBP keep their format.
    const encoded =
      sourceType === "image/png"
        ? canvas.toDataURL("image/png")
        : sourceType === "image/webp"
          ? canvas.toDataURL("image/webp", IMAGE_UPLOAD_QUALITY)
          : canvas.toDataURL("image/jpeg", IMAGE_UPLOAD_QUALITY);

    if (typeof encoded !== "string" || !encoded.startsWith("data:image/")) return originalDataUri;
    return encoded.length < originalDataUri.length ? encoded : originalDataUri;
  } catch (error) {
    return originalDataUri;
  }
};

// Request bodies on the upload routes are capped at 60 MB. Sizing what is
// actually being sent lets a big batch fail with a clear message instead of a
// generic "request too large".
const IMAGE_BATCH_MAX_PAYLOAD_CHARS = 50 * 1024 * 1024;
export const IMAGE_BATCH_TOO_LARGE_MESSAGE =
  "These photos are too large to send together. Please upload fewer at a time.";
export const exceedsImageBatchLimit = (dataUris = []) =>
  dataUris.reduce((total, uri) => total + String(uri || "").length, 0) > IMAGE_BATCH_MAX_PAYLOAD_CHARS;
